import { useEffect, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useSocket } from "../contexts/SocketContext";
import { fetchApplicationById } from "../redux/slices/applicationSlice";

/**
 * Hook for real-time application status updates via Socket.io.
 * Listens for `app_status`, `service_status`, and `deploy_progress` events.
 *
 * @param {string} applicationId - The application ID to listen for
 * @param {object} options - Optional callbacks
 * @param {function} options.onStatusChange - Called with { applicationId, status, message }
 * @param {function} options.onServiceStatus - Called with { serviceId, status, message }
 * @param {function} options.onDeployProgress - Called with progress entry
 */
export function useApplicationSocket(applicationId, options = {}) {
  const { socket } = useSocket();
  const dispatch = useDispatch();
  const { onStatusChange, onServiceStatus, onDeployProgress } = options;
  const [deployLogs, setDeployLogs] = useState([]);

  const refresh = useCallback(() => {
    if (applicationId) {
      dispatch(fetchApplicationById(applicationId));
    }
  }, [dispatch, applicationId]);

  const clearDeployLogs = useCallback(() => {
    setDeployLogs([]);
  }, []);

  useEffect(() => {
    if (!socket || !applicationId) return;

    const handleAppStatus = (data) => {
      if (data.applicationId === applicationId) {
        if (onStatusChange) onStatusChange(data);
        refresh();
      }
    };

    const handleServiceStatus = (data) => {
      if (data.applicationId === applicationId) {
        if (onServiceStatus) onServiceStatus(data);
        refresh();
      }
    };

    const handleDeployProgress = (data) => {
      if (data.applicationId === applicationId) {
        setDeployLogs((prev) => [...prev, data]);
        if (onDeployProgress) onDeployProgress(data);
      }
    };

    socket.on("app_status", handleAppStatus);
    socket.on("service_status", handleServiceStatus);
    socket.on("deploy_progress", handleDeployProgress);

    return () => {
      socket.off("app_status", handleAppStatus);
      socket.off("service_status", handleServiceStatus);
      socket.off("deploy_progress", handleDeployProgress);
    };
  }, [
    socket,
    applicationId,
    onStatusChange,
    onServiceStatus,
    onDeployProgress,
    refresh,
  ]);

  return { refresh, deployLogs, clearDeployLogs };
}
